import { fromEither, map, TaskEither, tryCatch } from 'fp-ts/lib/TaskEither';
import { fromPredicate, mapLeft } from 'fp-ts/lib/Either';
import { flow, pipe } from 'fp-ts/lib/function';
import { StatusCodes } from 'http-status-codes';
import { ApiError, decodeError, ParsedError } from './error';
import { PARSE_ERROR } from '../fetch';
import { SESSION_LIMIT_OR_UNAUTHORIZED, AI_CREDITS_EXHAUSTED } from '@/services/api';
import emitter from '@/services/emitter';

export class ApiResponse {
  public parsed: unknown;

  public ok: boolean;

  public status: number;

  public headers: Headers;

  constructor(
    ok: boolean,
    status: number,
    headers: Headers,
    parsed: unknown,
  ) {
    this.ok = ok;
    this.status = status;
    this.headers = headers;
    this.parsed = parsed;
  }
}

export const parse = (response: Response): TaskEither<ApiError, ApiResponse> => pipe(
  tryCatch(
    () => (
      // if the response is NO CONTENT, json parse would fail because it is an empty string
      // instead, we decode return an empty array
      response.status === StatusCodes.NO_CONTENT
        ? Promise.resolve([])
        : response.json()
    ),
    () => new ApiError(PARSE_ERROR, 'JSON Syntax Error'),
  ),
  map((jsonData: unknown) => new ApiResponse(
    response.ok,
    response.status,
    response.headers,
    jsonData,
  )),
);

export const parseBlob = (response: Response): TaskEither<ApiError, ApiResponse> => pipe(
  tryCatch(
    () => (
      // if the response is NO CONTENT, json parse would fail because it is an empty string
      // instead, we decode return an empty array
      response
        .status === StatusCodes.NO_CONTENT
        ? Promise.resolve(null)
        : Promise.resolve(response.blob())
    ),
    () => new ApiError(PARSE_ERROR, 'JSON Syntax Error'),
  ),
  map((blobData: unknown) => new ApiResponse(
    response.ok,
    response.status,
    response.headers,
    blobData,
  )),
);

export const validate = flow(
  fromPredicate<ParsedError, ApiResponse>(
    res => {
      if (res.status === SESSION_LIMIT_OR_UNAUTHORIZED) {
        emitter.emit('sessionLimitReached');
      }

      if (res.status === AI_CREDITS_EXHAUSTED) {
        emitter.emit('aiCreditsExhausted');
      }

      return res.ok;
    },
    res => ({
      code: res.status,
      parsed: res.parsed,
    }),
  ),
  mapLeft(decodeError),
  fromEither,
);
